import React, { useState, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";

import { motion, AnimatePresence } from "framer-motion";
import _ from "lodash";
import axios from "axios";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import DebugOverlay from "../components/widgets/DebugOverlay";
import NotificationWidget from "../components/widgets/NotificationWidget";

import Wrapper from "../components/layout/Wrapper";
import PreviewData from "../components/databasemanagement/PreviewData";
import LogoUploader from "../components/databasemanagement/LogoUploader";

// HOOKS
import useDatabaseManagement from "../hooks/useDatabaseManagement";
import useGlobals from "../hooks/useGlobals";

const SERVER_URL = window.location.protocol + "//" + window.location.host;

export default function DatabaseManagement({ parent }) {
   const [
      log,
      previewData,
      notification,
      {
         preview,
         insert,
         upsert,
         overwrite,
         initNewTournament,
         importCardsJSON,
         importCardImages,
         importStandings,
         importDecklists,
         importPlayers,
         checkForMissingImages,
         checkPlayerFlags,
         refreshTournament,
      },
   ] = useDatabaseManagement();

   const [
      globals,
      { updateGlobal, updateFormats, updateDraftSets, getGlobals },
   ] = useGlobals();

   const [newTournamentID, setNewTournamentID] = useState("");
   const [logoRefreshKey, setLogoRefreshKey] = useState(0);
   const [uniqueSets, setUniqueSets] = useState([]);
   const [draftSetsArray, setDraftSetsArray] = useState([]);
   const [tournamentList, setTournamentList] = useState([]);
   const [currentTournamentID, setCurrentTournamentID] = useState("");

   // Format date helper function
   const formatTournamentDate = (dateString) => {
      if (!dateString) return "";
      try {
         const date = new Date(dateString);
         return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
         });
      } catch (e) {
         return "";
      }
   };

   const updateNewTournamentID = (e) => {
      if (!isNaN(parseInt(e.target.value))) {
         setNewTournamentID(parseInt(e.target.value));
      }
   };

   const handleLogoUploaded = () => {
      // Refresh logo previews by updating the key
      setLogoRefreshKey((prev) => prev + 1);
   };

   const updateDraftSetValue = (e) => {
      updateDraftSets(JSON.parse(e.target.value));
   };

   // Fetch unique sets from database
   useEffect(() => {
      axios.get(SERVER_URL + "/api/getUniqueSets").then((response) => {
         setUniqueSets(response.data);
      });
   }, []);

   // Fetch tournament list from Melee
   useEffect(() => {
      axios.get(SERVER_URL + "/api/tournament/list").then((response) => {
         setTournamentList(response.data || []);
      });
   }, []);

   // Update currentTournamentID when globals change
   useEffect(() => {
      const tournamentIDGlobal = globals.find((g) => g.id === "TournamentID");
      if (tournamentIDGlobal && tournamentIDGlobal.data) {
         setCurrentTournamentID(tournamentIDGlobal.data);
         setNewTournamentID(tournamentIDGlobal.data);
      }
   }, [globals]);

   // Update draftSetsArray when globals change
   useEffect(() => {
      const draftSetsGlobal = globals.find((g) => g.id === "DraftSets");
      if (draftSetsGlobal && draftSetsGlobal.json) {
         setDraftSetsArray(Array.isArray(draftSetsGlobal.json) ? draftSetsGlobal.json : []);
      } else {
         setDraftSetsArray([]);
      }
   }, [globals]);

   const handleDraftSetChange = (index, value) => {
      const newArray = [...draftSetsArray];
      newArray[index] = value;
      setDraftSetsArray(newArray);
      // Filter out empty strings before saving
      const filteredArray = newArray.filter((set) => set && set !== "");
      updateDraftSets(filteredArray);
   };

   const handleAddDraftSet = () => {
      const newArray = [...draftSetsArray, ""];
      setDraftSetsArray(newArray);
   };

   const handleRemoveDraftSet = (index) => {
      const selectedSet = draftSetsArray[index];
      const setDisplayName = selectedSet
         ? uniqueSets.find((s) => s.set === selectedSet)?.set_name ||
         selectedSet.toUpperCase()
         : "this set";

      if (window.confirm(`Are you sure you want to remove ${setDisplayName}?`)) {
         const newArray = draftSetsArray.filter((_, i) => i !== index);
         setDraftSetsArray(newArray);
         // Filter out empty strings before saving
         const filteredArray = newArray.filter((set) => set && set !== "");
         updateDraftSets(filteredArray);
      }
   };

   useEffect(() => {
      getGlobals();
   }, []);

   useEffect(() => {
      // console.log(log);
   }, [log]);

   return (
      <Wrapper>
         <Typography
            variant='h3'
            component='div'
            sx={{ textTransform: "uppercase", mb: 2 }}
         >
            Database Management
         </Typography>

         <Typography
            variant='h4'
            component='div'
            sx={{ textTransform: "uppercase", mt: 4, mb: 2 }}
         >
            GLOBALS
         </Typography>

         <Grid
            container
            alignItems='center'
            spacing={2}
            sx={{ color: "#fff", backgroundColor: "#555", pb: 2 }}
         >
            {/* Always show draftRounds and formatRounds even if not in database */}
            {!globals.find((g) => g.id === "draftRounds") && (
               <React.Fragment>
                  <Grid item xs={3}>
                     <b>Draft Rounds</b>
                  </Grid>
                  <Grid item xs={9}>
                     <TextField
                        size='small'
                        variant='outlined'
                        type='number'
                        defaultValue={3}
                        onChange={(e) => {
                           const value = parseInt(e.target.value) || 0;
                           updateGlobal("draftRounds", value.toString());
                        }}
                        sx={{ minWidth: 150 }}
                     />
                  </Grid>
               </React.Fragment>
            )}

            {!globals.find((g) => g.id === "formatRounds") && (
               <React.Fragment>
                  <Grid item xs={3}>
                     <b>Constructed Rounds</b>
                  </Grid>
                  <Grid item xs={9}>
                     <TextField
                        size='small'
                        variant='outlined'
                        type='number'
                        defaultValue={5}
                        onChange={(e) => {
                           const value = parseInt(e.target.value) || 0;
                           updateGlobal("formatRounds", value.toString());
                        }}
                        sx={{ minWidth: 150 }}
                     />
                  </Grid>
               </React.Fragment>
            )}

            {globals &&
               globals.length > 0 &&
               globals.map((global, i) => (
                  <React.Fragment key={global + "-" + i}>
                     {global.id == "RoundByRoundTitles" && (
                        <Grid item xs={12}>
                           <Stack direction='row' spacing={2}>
                              <Button
                                 variant={
                                    global.json.includes("Draft")
                                       ? "contained"
                                       : "outlined"
                                 }
                                 color={
                                    global.json.includes("Draft")
                                       ? "error"
                                       : "primary"
                                 }
                                 onClick={() =>
                                    updateFormats(global.json, "Draft")
                                 }
                              >
                                 Draft
                              </Button>

                              <Button
                                 variant={
                                    global.json.includes("Standard")
                                       ? "contained"
                                       : "outlined"
                                 }
                                 color={
                                    global.json.includes("Standard")
                                       ? "error"
                                       : "primary"
                                 }
                                 onClick={() =>
                                    updateFormats(global.json, "Standard")
                                 }
                              >
                                 Standard
                              </Button>

                              <Button
                                 variant={
                                    global.json.includes("Pioneer")
                                       ? "contained"
                                       : "outlined"
                                 }
                                 color={
                                    global.json.includes("Pioneer")
                                       ? "error"
                                       : "primary"
                                 }
                                 onClick={() =>
                                    updateFormats(global.json, "Pioneer")
                                 }
                              >
                                 Pioneer
                              </Button>

                              <Button
                                 variant={
                                    global.json.includes("Modern")
                                       ? "contained"
                                       : "outlined"
                                 }
                                 color={
                                    global.json.includes("Modern")
                                       ? "error"
                                       : "primary"
                                 }
                                 onClick={() =>
                                    updateFormats(global.json, "Modern")
                                 }
                              >
                                 Modern
                              </Button>
                           </Stack>
                        </Grid>
                     )}
                     {global.id == "DraftSets" && (
                        <>
                           <Grid item xs={3}>
                              <b>Draft Sets</b>
                           </Grid>
                           <Grid item xs={9}>
                              <Stack direction='column' spacing={1}>
                                 {draftSetsArray.map((set, index) => (
                                    <Stack
                                       key={index}
                                       direction='row'
                                       spacing={1}
                                       alignItems='center'
                                    >
                                       <FormControl
                                          size='small'
                                          sx={{ minWidth: 400 }}
                                       >
                                          <Select
                                             value={set || ""}
                                             displayEmpty
                                             onChange={(e) =>
                                                handleDraftSetChange(
                                                   index,
                                                   e.target.value
                                                )
                                             }
                                             sx={{ width: 400, backgroundColor: "white" }}
                                          >
                                             <MenuItem value=''>
                                                <em>None</em>
                                             </MenuItem>
                                             {uniqueSets.map((setOption) => (
                                                <MenuItem
                                                   key={setOption.set}
                                                   value={setOption.set}
                                                >
                                                   {setOption.set_name
                                                      ? `${setOption.set_name} (${setOption.set.toUpperCase()})`
                                                      : setOption.set.toUpperCase()}
                                                </MenuItem>
                                             ))}
                                          </Select>
                                       </FormControl>
                                       <IconButton
                                          color='error'
                                          size='small'
                                          onClick={() =>
                                             handleRemoveDraftSet(index)
                                          }
                                       >
                                          <DeleteIcon />
                                       </IconButton>
                                    </Stack>
                                 ))}
                                 <Button
                                    variant='outlined'
                                    startIcon={<AddIcon />}
                                    onClick={handleAddDraftSet}
                                    size='small'
                                    sx={{ alignSelf: "flex-start" }}
                                 >
                                    ADD
                                 </Button>
                              </Stack>
                           </Grid>
                        </>
                     )}

                     {global.id == "TournamentFormat" && (
                        <>
                           <Grid item xs={3}>
                              <b>Deck Format</b>
                           </Grid>
                           <Grid item xs={9}>
                              <Stack direction='row' spacing={2} alignItems='center'>
                                 {global.data && (
                                    <TextField
                                       disabled
                                       size='small'
                                       variant='outlined'
                                       value={global.data}
                                       sx={{ minWidth: 150 }}
                                    />
                                 )}
                                 <Button
                                    variant={
                                       global.data === "STANDARD"
                                          ? "contained"
                                          : "outlined"
                                    }
                                    color={
                                       global.data === "STANDARD"
                                          ? "primary"
                                          : "inherit"
                                    }
                                    onClick={() =>
                                       updateGlobal("TournamentFormat", "STANDARD")
                                    }
                                 >
                                    Standard
                                 </Button>
                                 <Button
                                    variant={
                                       global.data === "PIONEER"
                                          ? "contained"
                                          : "outlined"
                                    }
                                    color={
                                       global.data === "PIONEER"
                                          ? "primary"
                                          : "inherit"
                                    }
                                    onClick={() =>
                                       updateGlobal("TournamentFormat", "PIONEER")
                                    }
                                 >
                                    Pioneer
                                 </Button>
                                 <Button
                                    variant={
                                       global.data === "MODERN"
                                          ? "contained"
                                          : "outlined"
                                    }
                                    color={
                                       global.data === "MODERN"
                                          ? "primary"
                                          : "inherit"
                                    }
                                    onClick={() =>
                                       updateGlobal("TournamentFormat", "MODERN")
                                    }
                                 >
                                    Modern
                                 </Button>
                              </Stack>
                           </Grid>
                        </>
                     )}

                     {global.id == "draftRounds" && (
                        <>
                           <Grid item xs={3}>
                              <b>Draft Rounds</b>
                           </Grid>
                           <Grid item xs={9}>
                              <TextField
                                 size='small'
                                 variant='outlined'
                                 type='number'
                                 value={global.data || 3}
                                 onChange={(e) => {
                                    const value = parseInt(e.target.value) || 0;
                                    updateGlobal("draftRounds", value.toString());
                                 }}
                                 sx={{ minWidth: 150 }}
                              />
                           </Grid>
                        </>
                     )}

                     {global.id == "formatRounds" && (
                        <>
                           <Grid item xs={3}>
                              <b>Constructed Rounds</b>
                           </Grid>
                           <Grid item xs={9}>
                              <TextField
                                 size='small'
                                 variant='outlined'
                                 type='number'
                                 value={global.data || 5}
                                 onChange={(e) => {
                                    const value = parseInt(e.target.value) || 0;
                                    updateGlobal("formatRounds", value.toString());
                                 }}
                                 sx={{ minWidth: 150 }}
                              />
                           </Grid>
                        </>
                     )}

                     {global.id != "RoundByRoundTitles" &&
                        global.id != "DraftSets" &&
                        global.id != "TournamentFormat" &&
                        global.id != "draftRounds" &&
                        global.id != "formatRounds" && (
                           <>
                              <Grid item xs={3}>
                                 <b>{global.id}</b>
                              </Grid>
                              <Grid item xs={9}>
                                 {global.data && (
                                    <TextField
                                       disabled
                                       size='small'
                                       variant='outlined'
                                       value={global.data}
                                    />
                                 )}

                                 {global.json && (
                                    <TextField
                                       disabled
                                       size='small'
                                       variant='outlined'
                                       value={JSON.stringify(global.json)}
                                    />
                                 )}
                              </Grid>
                           </>
                        )}
                  </React.Fragment>
               ))}
         </Grid>



         <Typography
            variant='h4'
            component='div'
            sx={{ textTransform: "uppercase", mt: 4, mb: 2 }}
         >
            LOGO
         </Typography>

         <Grid
            container
            alignItems='center'
            spacing={2}
            sx={{ color: "#fff", backgroundColor: "#555", pb: 2, mb: 2 }}
         >
            <Grid item xs={12}>
               <Stack direction='row' spacing={2} alignItems='center'>
                  <Stack direction='row' spacing={1} alignItems='center'>
                     <Box
                        sx={{
                           border: "1px solid #666",
                           borderRadius: 1,
                           p: 0.5,
                           backgroundColor: "#222",
                        }}
                     >
                        <img
                           key={`logo-800-${logoRefreshKey}`}
                           src={
                              SERVER_URL +
                              "/images/ui/logo-800.png?t=" +
                              logoRefreshKey
                           }
                           alt='Logo 800px'
                           style={{
                              height: "200px",
                              width: "auto",
                              display: "block",
                           }}
                           onError={(e) => {
                              e.target.style.display = "none";
                           }}
                        />
                     </Box>
                     <Box
                        sx={{
                           border: "1px solid #666",
                           borderRadius: 1,
                           p: 0.5,
                           backgroundColor: "#222",
                        }}
                     >
                        <img
                           key={`logo-115-${logoRefreshKey}`}
                           src={
                              SERVER_URL +
                              "/images/ui/logo-115.png?t=" +
                              logoRefreshKey
                           }
                           alt='Logo 115px'
                           style={{
                              height: "80px",
                              width: "auto",
                              display: "block",
                           }}
                           onError={(e) => {
                              e.target.style.display = "none";
                           }}
                        />
                     </Box>
                  </Stack>
                  <Stack direction='column' spacing={2} alignItems='flex-start'>
                     <LogoUploader callback={handleLogoUploaded} />
                     <Typography variant='body2' sx={{ color: "#aaa" }}>
                        Upload PNG logo.  Crops off edge transparency, and sizes to fit in 640x800 and 92x115
                     </Typography>
                  </Stack>
               </Stack>
            </Grid>
         </Grid>

         {parent != "producer" && (
            <>
               <Typography
                  variant='h4'
                  component='div'
                  sx={{ textTransform: "uppercase", mt: 8, mb: 2 }}
               >
                  MTG MELEE
               </Typography>

               <Typography
                  variant='h5'
                  component='div'
                  sx={{ textTransform: "uppercase", mb: 2 }}
               >
                  IMPORT MELEE DATA
               </Typography>
               <Grid
                  container
                  spacing={3}
                  sx={{ mb: 4, color: "#fff" }}
                  alignItems='center'
               >
                  {/* <Grid item xs={4}>
                     <Button
                        variant='contained'
                        color='success'
                        onClick={importStandings}
                        fullWidth
                     >
                        Current Standings
                     </Button>
                  </Grid>
                  <Grid item xs={8}>
                     Import/Update the Current Standings
                  </Grid> */}

                  {/* <Grid item xs={4}>
                     <Button
                        variant='contained'
                        color='success'
                        onClick={() => importDecklists("insert")}
                        fullWidth
                     >
                        Decklists
                     </Button>
                  </Grid>
                  <Grid item xs={8}>
                     Import/Update all Player Decklists
                  </Grid> */}

                  <Grid item xs={4}>
                     <Button
                        variant='contained'
                        color='success'
                        onClick={() => importPlayers("insert")}
                        fullWidth
                     >
                        Update All
                     </Button>
                  </Grid>
                  <Grid item xs={8}>
                     Import/Update all Players and Decklists
                  </Grid>
               </Grid>

               <Typography
                  variant='h5'
                  component='div'
                  sx={{ textTransform: "uppercase", mb: 2 }}
               >
                  VERIFY MELEE DATA
               </Typography>

               <Grid
                  container
                  spacing={3}
                  sx={{ mb: 4, color: "#fff" }}
                  alignItems='center'
               >
                  <Grid item xs={4}>
                     <Button
                        variant='contained'
                        color='secondary'
                        onClick={checkForMissingImages}
                        fullWidth
                     >
                        Check for Missing Card Images
                     </Button>
                  </Grid>
                  <Grid item xs={8}>
                     Compares currently cached Melee decklist cards and card
                     library
                  </Grid>
                  <Grid item xs={4}>
                     <Button
                        variant='contained'
                        color='secondary'
                        onClick={checkPlayerFlags}
                        fullWidth
                     >
                        Check for Player Country Flags
                     </Button>
                  </Grid>
                  <Grid item xs={8}>
                     Checks to see if player Metadata "COUNTRY" matches a flag
                     image
                  </Grid>
               </Grid>
            </>
         )}
         <Stack sx={{ color: "#fff", mt: 8 }} spacing={2}>
            {log &&
               log.length > 0 &&
               log.map((row, i) => (
                  <Grid container key={"log-" + i + "-" + row.timestamp}>
                     <Grid item xs={2}>
                        {row.message}
                        <br />
                        <i>
                           {new Date(row.timestamp).toLocaleTimeString("en-US")}
                        </i>
                     </Grid>
                     <Grid
                        item
                        xs={10}
                        sx={{
                           width: "100%",
                           maxHeight: 500,
                           overflow: "scroll",
                           border: "1px solid #ccc",
                        }}
                     >
                        <pre>{JSON.stringify(row.response.data, null, 4)}</pre>
                     </Grid>
                  </Grid>
               ))}
         </Stack>

         {parent == "admin" && (
            <>
               <Typography
                  variant='h4'
                  component='div'
                  sx={{ textTransform: "uppercase", mt: 8, mb: 2 }}
               >
                  NO TOUCHY! BOSS MODE ONLY
               </Typography>
               <Grid
                  container
                  spacing={3}
                  sx={{ mb: 4, color: "#fff" }}
                  alignItems='center'
               >
                  <Grid item xs={4}>
                     <Button
                        variant='contained'
                        color='warning'
                        onClick={refreshTournament}
                        fullWidth
                     >
                        Refresh Tournament Round Data
                     </Button>
                  </Grid>
                  <Grid item xs={8}>
                     Updates Tournament data in Evoke—use for Round information
                     for Standings
                  </Grid>
                  {/* <Grid item xs={4}>
                     <Button
                        variant='contained'
                        color='warning'
                        onClick={() => importDecklists(true)}
                        fullWidth
                     >
                        Overwrite Decklists
                     </Button>
                  </Grid>
                  <Grid item xs={8}>
                     Deletes all existing decklists and re-adds current MTG
                     Melee decklists. Only use if you have test decklists from
                     an old test TournamentID, or if you experienced Metadata
                     errors that have been corrected (related to Decklist ID #'s
                     being overwritten).
                  </Grid> */}
                  <Grid item xs={4}>
                     <Button
                        variant='contained'
                        color='warning'
                        onClick={() => importPlayers("truncate")}
                        fullWidth
                     >
                        Overwrite All Players and Decklists
                     </Button>
                  </Grid>
                  <Grid item xs={8}>
                     Deletes all existing players and re-adds current MTG Melee
                     Tournament Players. Only use if you have players from and
                     old TournamentID, or if you are seeing duplicate players,
                     or need to remove players that were registered, but have
                     since been removed from the tournament.
                  </Grid>
               </Grid>

               <Typography
                  variant='h4'
                  component='div'
                  sx={{ textTransform: "uppercase", mt: 8 }}
               >
                  RE-INITIALIZE
               </Typography>

               <Typography
                  variant='h5'
                  component='div'
                  sx={{ textTransform: "uppercase", mb: 2 }}
               >
                  These controls are "breaking". They will irreversibly delete
                  content from the system, overwrite card and image data, and
                  change the tournament ID currently be accessed.
                  <br />
                  <b style={{ color: "#f00" }}>USE WITH EXTREME CAUTION</b>
               </Typography>
               <Grid
                  container
                  spacing={3}
                  sx={{ mb: 4, color: "#fff" }}
                  alignItems='center'
               >
                  <Grid item xs={4}>
                     <Button
                        variant='contained'
                        color='error'
                        onClick={() =>
                           updateGlobal("TournamentID", newTournamentID)
                        }
                        fullWidth
                     >
                        Update Tournament ID
                     </Button>
                  </Grid>
                  <Grid item xs={8}>
                     <Stack direction='row' spacing={2} alignItems='center'>
                        <TextField
                           value={newTournamentID}
                           size='small'
                           variant='outlined'
                           label='Enter Tournament ID'
                           onChange={updateNewTournamentID}
                        />
                                 <FormControl size='small' sx={{ minWidth: 400 }}>
                                    <Select
                                       value={currentTournamentID || ""}
                                       displayEmpty
                                       onChange={(e) => {
                                          const selectedId = e.target.value.toString();
                                          setNewTournamentID(selectedId);
                                          setCurrentTournamentID(selectedId);
                                       }}
                                       sx={{ width: 400, backgroundColor: "white" }}
                                    >
                                       <MenuItem value=''>
                                          <em>Select Tournament</em>
                                       </MenuItem>
                                       {tournamentList.map((tournament) => (
                                          <MenuItem key={tournament.id} value={tournament.id}>
                                             {tournament.name}
                                             {tournament.startDate && ` (${formatTournamentDate(tournament.startDate)})`}
                                          </MenuItem>
                                       ))}
                                    </Select>
                                 </FormControl>
                     </Stack>
                     <br />
                     Allows you to change TournamentID #'s. Doing so does not
                     empty out old data from system, but does make Melee use the
                     TournamentID for all future sourced data.
                  </Grid>
                  <Grid item xs={4}>
                     <Button
                        variant='contained'
                        color='error'
                        onClick={() => initNewTournament()}
                        fullWidth
                     >
                        CLEAR ALL DATA / RE-INITIALIZE
                     </Button>
                  </Grid>
                  <Grid item xs={8}>
                     Only use at start of show. Clears all data from the system.
                     Must run all MTG MELEE Preview routines above after
                     initialization to get players and decklsits back into the
                     system.
                  </Grid>

                  <Grid item xs={4} sx={{ mt: 2 }}>
                     <Button
                        variant='contained'
                        color='error'
                        onClick={importCardsJSON}
                        fullWidth
                     >
                        Import JSON Card Data
                     </Button>
                  </Grid>
                  <Grid item xs={8}>
                     This imports a JSON card database. Should never be used.
                     (Location: /server/volume/imports/cards.json)
                  </Grid>

                  <Grid item xs={4}>
                     <Button
                        variant='contained'
                        color='error'
                        onClick={importCardImages}
                        fullWidth
                     >
                        Import Card Images
                     </Button>
                  </Grid>
                  <Grid item xs={8}>
                     This imports all the card images into the Database.
                     (Location: /server/volume/cardimages/jpg)
                  </Grid>
               </Grid>
            </>
         )}

         <PreviewData
            data={previewData}
            callback={(runUpdate) => upsert(runUpdate)}
         />

         <NotificationWidget notification={notification} />
      </Wrapper>
   );
}
